import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
const API = "/api";

// B17 — oferty AliExpress. Kontrakt backendu (backend/aliexpress_offers.py):
//   GET  /api/aliexpress-offers?page&status&q        -> {offers,total,page,pages}
//   GET  /api/aliexpress-offers/stock?ids=pid,...     -> {stocks:{pid:{total,n,
//        variants:[{sku_id,sku_code,ean,price,currency,label,stock,active,image}]}}}
//   GET  /api/aliexpress-offers/search?q&status       -> {index:{state,done,total,
//        built_at,products,stale}, results:[...], matched}   (B17: EAN/SKU/nr/nazwa PL)
//   GET  /api/aliexpress-offers/{product_id}          -> szczegoly + skus[]
//   PATCH /api/aliexpress-offers/{product_id}         -> {ok,updated,errors,verify,product}
// Zmiany B17 (wg zalecen uzytkownika):
//   - warianty wprost na liscie: zdjecie na wysokosc 2 linii, obok warianty
//     (kazdy w osobnym wierszu), ILOŚĆ w tej samej kolumnie co dotychczas,
//     CENA osobny wiersz per wariant (koniec z "od-do"); przy >2 wariantach
//     zdjecie NIE rosnie (zostaje 2-liniowe), produkt z 1 wariantem = 1 linia
//     jak dotychczas;
//   - klik w nr produktu kopiuje go (takze SKU/EAN w kolumnie wariantu);
//   - na liscie widoczne SKU i EAN per wariant;
//   - wyszukiwanie po EAN, SKU, nr produktu lub nazwie (/search; pierwszy raz
//     backend buduje indeks w tle ~1 min — pasek postepu);
//   - edycja masowa: raport WERYFIKACJI zapisu (backend porownuje zadane vs
//     odczytane z AE; "AE nie zastosował..." -> ⚠ w logu masowki).
// B16D: pasek edycji masowej + panel wyniku. B16F: modal "🔢 Ilości wariantów".

type AeOffer = {
  product_id: number;
  subject: string | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
  image: string | null;
  gmt_create: string | null;
  gmt_modified: string | null;
  stock: number | null;
};

type AeSku = {
  sku_id: string;
  sku_code: string | null;
  price: number;
  currency: string | null;
  stock: number;
  ean: string | null;
  active: boolean;
  label: string | null;
  image: string | null;
};

type AeProduct = {
  product_id: number;
  subject: string | null;
  status: string | null;
  price: number | null;
  currency: string | null;
  images: string[];
  image: string | null;
  category_id: number | null;
  gmt_create: string | null;
  gmt_modified: string | null;
  weight_kg: number | null;
  package_lwh: (number | null)[];
  url: string;
  skus: AeSku[];
};

type AeStockVar = {              // B17: wariant w kolumnach listy (z /stock)
  sku_id: string;
  sku_code?: string | null;
  ean?: string | null;
  price?: number | null;
  currency?: string | null;
  label: string | null;
  stock: number;
  active: boolean;
  image: string | null;
};

type AeStockInfo = {
  total: number;
  n: number;
  variants: AeStockVar[];
  error?: string;
};

type IdxInfo = {                 // B17: stan indeksu wyszukiwania
  state: 'building' | 'ready';
  done?: number;
  total?: number;
  built_at?: string | null;
  products?: number;
  stale?: boolean;
};

type QtyItem = {
  pid: number;
  subject: string | null;
  image: string | null;
  skus: AeSku[];
  edit: { [skuId: string]: string };
  error?: string;
};

const LIMIT = 50;

const s: { [k: string]: React.CSSProperties } = {
  input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: '#fff' },
  btn: { border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer', background: '#fff' },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 },
};

const auth = () => ({ headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });

const fmtPrice = (v: number | null | undefined, cur?: string | null) =>
  (v == null ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) + (cur ? ' ' + cur : '');

const fmtDT = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const p = (x: number) => String(x).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
};

const aeUrl = (pid: number) => 'https://pl.aliexpress.com/item/' + pid + '.html';

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : ((n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? few : many);
const warLabel = (n: number) => plural(n, 'wariant', 'warianty', 'wariantów');
const prodLabel = (n: number) => plural(n, 'produkt', 'produkty', 'produktów');

export default function AliexpressOffers() {
  // --- lista / wyszukiwanie ---
  const [offers, setOffers] = useState<AeOffer[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('onSale');
  const [searchMode, setSearchMode] = useState(false);       // B17
  const [idxInfo, setIdxInfo] = useState<IdxInfo | null>(null);  // B17

  // --- szczegoly + edycja ---
  const [det, setDet] = useState<AeProduct | null>(null);
  const [detLoading, setDetLoading] = useState(false);
  const [edit, setEdit] = useState<{ [skuId: string]: { price: string; stock: string } }>({});
  const [saving, setSaving] = useState(false);

  // --- edycja masowa ---
  const [sel, setSel] = useState<{ [pid: string]: AeOffer }>({});
  const selArr = Object.values(sel);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ price: '', stock: '' });
  const [bulkRun, setBulkRun] = useState({ running: false, done: 0, total: 0, current: '' });
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const bulkCancel = useRef(false);

  // --- B17: kolumna Ilość — warianty wprost w wierszach ---
  const [stocks, setStocks] = useState<{ [pid: string]: AeStockInfo }>({});
  const [copied, setCopied] = useState('');
  // --- B16F: modal hurtowej zmiany ilosci per wariant ---
  const [qtyOpen, setQtyOpen] = useState(false);
  const [qtyItems, setQtyItems] = useState<QtyItem[]>([]);
  const [qtyPhase, setQtyPhase] = useState<'load' | 'form' | 'run'>('load');
  const [qtyRun, setQtyRun] = useState({ done: 0, total: 0, current: '' });
  const [qtyAll, setQtyAll] = useState<{ [pid: string]: string }>({});
  const qtyCancel = useRef(false);

  const load = useCallback(async (p: number) => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(API + '/aliexpress-offers', {
        params: { page: p, page_size: LIMIT, status: statusF, ...(q ? { q } : {}) },
        ...auth(),
      });
      setOffers(r.data?.offers || []);
      setTotal(r.data?.total || 0);
      setPages(Math.max(1, r.data?.pages || 1));
      setPage(p);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'błąd pobierania listy AliExpress');
    } finally {
      setLoading(false);
    }
  }, [q, statusF]);

  // B17: ladowanie listy ALBO wyszukiwania (z poll-em budowy indeksu w tle)
  useEffect(() => {
    let dead = false;
    let timer = 0;
    const tick = async () => {
      if (dead) return;
      setSearchLoading(true); setErr('');
      let keepLoading = false;
      try {
        const r = await axios.get(API + '/aliexpress-offers/search', {
          params: { q, status: statusF }, ...auth(),
        });
        if (dead) return;
        const idx: IdxInfo | null = r.data?.index || null;
        setIdxInfo(idx);
        if (idx && idx.state === 'building') {
          keepLoading = true;
          timer = window.setTimeout(tick, 2500);
          return;
        }
        const res: AeOffer[] = r.data?.results || [];
        setOffers(res);
        setTotal(r.data?.matched != null ? r.data.matched : res.length);
        setPages(1); setPage(1);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'błąd wyszukiwania');
      } finally {
        if (!keepLoading && !dead) setSearchLoading(false);
      }
    };
    if (q) {
      setSearchMode(true);
      setOffers([]);
      tick();
    } else {
      setSearchMode(false);
      setIdxInfo(null);
      load(1);
    }
    return () => { dead = true; if (timer) clearTimeout(timer); };
  }, [q, statusF]); // eslint-disable-line react-hooks/exhaustive-deps

  // stany magazynowe widocznej strony/wyników — jedno zapytanie /stock w tle
  // (max 50 id = LIMIT; backend keszuje 120 s)
  useEffect(() => {
    if (!offers.length) return;
    const ids = offers.map(o => String(o.product_id)).join(',');
    let dead = false;
    axios.get(API + '/aliexpress-offers/stock', { params: { ids }, ...auth() })
      .then(resp => { if (!dead) setStocks(prev => ({ ...prev, ...(resp.data?.stocks || {}) })); })
      .catch(() => { /* kolumna zostaje na "…" lub wartosci z listy */ });
    return () => { dead = true; };
  }, [offers]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = () => { setQ(searchInput.trim()); };
  const clearSearch = () => { setSearchInput(''); setQ(''); };
  const refresh = () => { load(page); setMsg(''); };

  // B17: kopiowanie do schowka (nr produktu / SKU / EAN) — z fallbackiem
  const copyText = (text: string, tag: string) => {
    const done = () => {
      setCopied(tag);
      window.setTimeout(() => setCopied(''), 1300);
    };
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* ignoruj */ }
      done();
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done, fallback);
    } else {
      fallback();
    }
  };

  const toggleSel = (o: AeOffer) => {
    setSel(prev => {
      const n = { ...prev };
      if (n[String(o.product_id)]) delete n[String(o.product_id)];
      else n[String(o.product_id)] = o;
      return n;
    });
  };
  const allPageSelected = offers.length > 0 && offers.every(o => sel[String(o.product_id)]);
  const toggleAllPage = () => {
    setSel(prev => {
      const n = { ...prev };
      if (allPageSelected) offers.forEach(o => delete n[String(o.product_id)]);
      else offers.forEach(o => { n[String(o.product_id)] = o; });
      return n;
    });
  };

  // --- szczegoly ---
  const openDetails = async (pid: number) => {
    setDetLoading(true); setDet(null); setEdit({}); setErr('');
    try {
      const r = await axios.get(API + '/aliexpress-offers/' + pid, auth());
      const d: AeProduct = r.data;
      setDet(d);
      const e: { [k: string]: { price: string; stock: string } } = {};
      d.skus.forEach(k => { e[k.sku_id] = { price: String(k.price ?? ''), stock: String(k.stock ?? '') }; });
      setEdit(e);
      setStocks(prev => {
        const key = String(d.product_id);
        if (prev[key] && !prev[key].error) return prev;
        return { ...prev, [key]: {
          total: d.skus.reduce((a, k) => a + (k.stock || 0), 0),
          n: d.skus.length,
          variants: d.skus.map(k => ({ sku_id: k.sku_id, sku_code: k.sku_code, ean: k.ean,
            price: k.price, currency: k.currency, label: k.label, stock: k.stock, active: k.active, image: k.image })),
        } };
      });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'błąd pobierania szczegółów');
    } finally {
      setDetLoading(false);
    }
  };

  const changedCount = (): number => {
    if (!det) return 0;
    let n = 0;
    det.skus.forEach(k => {
      const e = edit[k.sku_id];
      if (!e) return;
      const p = parseFloat(String(e.price).replace(',', '.'));
      const st = parseInt(String(e.stock), 10);
      if (!isNaN(p) && Math.abs(p - (k.price ?? 0)) > 0.000001) n++;
      else if (!isNaN(st) && st !== (k.stock ?? 0)) n++;
      else if (String(e.price).trim() === '' || String(e.stock).trim() === '') n++;
    });
    return n;
  };

  const saveDetails = async () => {
    if (!det) return;
    const skus: any[] = [];
    det.skus.forEach(k => {
      const e = edit[k.sku_id];
      if (!e) return;
      const p = parseFloat(String(e.price).replace(',', '.'));
      const st = parseInt(String(e.stock), 10);
      const item: any = { sku_id: k.sku_id, sku_code: k.sku_code || undefined };
      let touched = false;
      if (!isNaN(p) && Math.abs(p - (k.price ?? 0)) > 0.000001) { item.price = p; touched = true; }
      if (!isNaN(st) && st !== (k.stock ?? 0)) { item.inventory = st; touched = true; }
      if (touched) skus.push(item);
    });
    if (!skus.length) { setMsg('Brak zmian do zapisania.'); return; }
    setSaving(true); setErr(''); setMsg('Zapisuję…');
    try {
      const r = await axios.patch(API + '/aliexpress-offers/' + det.product_id, { skus }, auth());
      if (r.data?.product) {
        const d: AeProduct = r.data.product;
        setDet(d);
        const e: { [k: string]: { price: string; stock: string } } = {};
        d.skus.forEach(k => { e[k.sku_id] = { price: String(k.price ?? ''), stock: String(k.stock ?? '') }; });
        setEdit(e);
      }
      const up = (r.data?.updated || []).length;
      const er = (r.data?.errors || []);
      setMsg('✔ Zapisano zmiany wariantów: ' + up + (er.length ? ' (⚠ ' + er.length + ')' : ''));
      if (er.length) setErr('⚠ ' + er.join('; '));
      if (searchMode) { setQ(q); } else { load(page); }
      refreshStocks([String(det.product_id)]);
    } catch (e: any) {
      setMsg('');
      setErr(e?.response?.data?.detail || 'błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  // --- edycja masowa: ustaw cene/stan na WSZYSTKIE warianty zaznaczonych produktow ---
  const runBulk = async (stockZero = false) => {
    const targets = selArr;
    if (!targets.length) return;
    const price = stockZero ? null : (bulkForm.price.trim() === '' ? null : parseFloat(bulkForm.price.replace(',', '.')));
    const stock = stockZero ? 0 : (bulkForm.stock.trim() === '' ? null : parseInt(bulkForm.stock, 10));
    if (price === null && stock === null) { setBulkLog(['Podaj cenę i/lub stan.']); return; }
    if (price !== null && (isNaN(price) || price <= 0)) { setBulkLog(['Niepoprawna cena.']); return; }
    if (stock !== null && (isNaN(stock) || stock < 0)) { setBulkLog(['Niepoprawny stan.']); return; }
    bulkCancel.current = false;
    setBulkOpen(true);
    setBulkRun({ running: true, done: 0, total: targets.length, current: '' });
    setBulkLog([]);
    const log: string[] = [];
    let ok = 0, fail = 0, warn = 0;
    for (let i = 0; i < targets.length; i++) {
      if (bulkCancel.current) { log.push('⏹ Przerwano po ' + i + ' z ' + targets.length); break; }
      const o = targets[i];
      setBulkRun(r => ({ ...r, done: i, current: o.subject || String(o.product_id) }));
      try {
        const d = await axios.get(API + '/aliexpress-offers/' + o.product_id, auth());
        const skus = ((d.data?.skus) || []).map((k: any) => ({
          sku_id: k.sku_id, sku_code: k.sku_code || undefined,
          ...(price !== null ? { price } : {}), ...(stock !== null ? { inventory: stock } : {}),
        }));
        if (!skus.length) { fail++; log.push('✖ ' + (o.subject || o.product_id) + ': brak wariantów'); continue; }
        // B17: odpowiedz PATCH niesie weryfikacje zapisu (errors = rozjazdy vs AE)
        const r = await axios.patch(API + '/aliexpress-offers/' + o.product_id, { skus }, auth());
        const w = (r.data?.errors) || [];
        if (w.length) { warn++; log.push('⚠ ' + (o.subject || o.product_id) + ': ' + w.join(' · ')); }
        else { ok++; log.push('✔ ' + (o.subject || o.product_id)); }
      } catch (e: any) {
        fail++; log.push('✖ ' + (o.subject || o.product_id) + ': ' + (e?.response?.data?.detail || 'błąd'));
      }
      await new Promise<void>(res => setTimeout(res, 300));
    }
    setBulkRun(r => ({ ...r, running: false, done: targets.length, current: '' }));
    setBulkOpen(false);
    setBulkLog(['✔ Zapisane: ' + ok + ' / ' + targets.length
      + (warn ? ' · ⚠ niepotwierdzone przez AE: ' + warn : '')
      + (fail ? ' · ✖ błędy: ' + fail : ''), '— szczegóły —', ...log]);
    setSel({});
    if (searchMode) { setQ(q); } else { load(page); }
    const pids = targets.map(o => String(o.product_id));
    refreshStocks(pids);
    // B17: AE potrafi podac spojny odczyt z opoznieniem — kontrolne odswiezenie
    window.setTimeout(() => refreshStocks(pids), 9000);
  };

  // ---------------- B17: kolumna Ilość + warianty ----------------

  const refreshStocks = async (pids: string[]) => {
    if (!pids.length) return;
    try {
      const r = await axios.get(API + '/aliexpress-offers/stock', { params: { ids: pids.join(',') }, ...auth() });
      setStocks(prev => ({ ...prev, ...(r.data?.stocks || {}) }));
    } catch { /* cicho — odswiezy sie przy zmianie strony */ }
  };

  const priceCell = (o: AeOffer) => {
    if (o.price_min == null && o.price_max == null) return '—';
    if (o.price_min != null && o.price_max != null && Math.abs(o.price_min - o.price_max) > 0.000001)
      return fmtPrice(o.price_min) + ' – ' + fmtPrice(o.price_max) + ' ' + (o.currency || '');
    return fmtPrice(o.price_min != null ? o.price_min : o.price_max, o.currency);
  };

  const stockFallback = (o: AeOffer, si: AeStockInfo | undefined) => {
    const total = si ? (si.error ? o.stock : si.total) : o.stock;
    if (total == null) {
      return <span style={{ color: si && si.error ? '#b91c1c' : '#9ca3af' }} title={(si && si.error) || ''}>{si && si.error ? '!' : '…'}</span>;
    }
    return <b style={{ color: total === 0 ? '#b91c1c' : '#111827' }}>{total}</b>;
  };

  // B17: caly blok produktu — N wierszy (po 1 na wariant), zdjecie/nazwa/checkbox/
  // data/akcje z rowspan; przy braku danych /stock fallback na 1 wiersz agregowany
  const renderOffer = (o: AeOffer) => {
    const key = String(o.product_id);
    const si = stocks[key];
    const vars = (si && !si.error) ? (si.variants || []) : [];
    const useVars = vars.length > 0;
    const N = useVars ? vars.length : 1;
    const selected = !!sel[key];
    const bg = selected ? '#eff6ff' : undefined;
    const imgPx = N >= 2 ? 72 : 40;      // zdjęcie: 2 linie gdy >=2 warianty (B17)
    const trs: React.ReactElement[] = [];
    for (let i = 0; i < N; i++) {
      const v = useVars ? vars[i] : null;
      const dim = v && !v.active ? 0.55 : 1;
      const code = v ? (v.sku_code || null) : null;
      const ean = v ? (v.ean || null) : null;
      trs.push(
        <tr key={key + '-' + i} style={bg ? { background: bg } : undefined}>
          {i === 0 && (
            <td rowSpan={N} style={{ ...s.td, verticalAlign: 'top' }}>
              <input type="checkbox" checked={selected} onChange={() => toggleSel(o)} title="Zaznacz do edycji masowej" />
            </td>
          )}
          {i === 0 && (
            <td rowSpan={N} style={{ ...s.td, verticalAlign: 'top' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ width: imgPx, height: imgPx, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {o.image ? (
                    <img src={o.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={e => { const t = e.target as HTMLImageElement; if (t && t.style) t.style.visibility = 'hidden'; }} />
                  ) : (
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>brak</span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, maxWidth: 420 }}>{o.subject || '—'}</div>
                  <span data-b17="copyid" onClick={() => copyText(String(o.product_id), 'pid' + key)}
                    title="Kliknij, aby skopiować nr produktu"
                    style={{ fontSize: 11, color: copied === 'pid' + key ? '#059669' : '#6b7280', fontFamily: 'monospace', cursor: 'copy', userSelect: 'none', display: 'inline-block', marginTop: 2 }}>
                    {copied === 'pid' + key ? '✔ skopiowano' : o.product_id}
                  </span>
                </div>
              </div>
            </td>
          )}
          <td style={s.td}>
            {v ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', opacity: dim }} title={v.active ? '' : 'wariant nieaktywny'}>
                {v.image && (
                  <img src={v.image} alt="" loading="lazy"
                    style={{ width: 26, height: 26, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', flexShrink: 0 }} />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#374151' }}>{v.label || v.sku_id}</div>
                  {(code || ean) && (
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {code && (
                        <span data-b17="copysku" onClick={() => copyText(code, 'sku' + v.sku_id)} title="Kliknij, aby skopiować SKU"
                          style={{ cursor: 'copy', color: copied === 'sku' + v.sku_id ? '#059669' : undefined }}>
                          {'SKU: ' + code}
                        </span>
                      )}
                      {code && ean ? ' · ' : ''}
                      {ean && (
                        <span onClick={() => copyText(ean, 'ean' + v.sku_id)} title="Kliknij, aby skopiować EAN"
                          style={{ cursor: 'copy', color: copied === 'ean' + v.sku_id ? '#059669' : undefined }}>
                          {'EAN: ' + ean}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : !si ? (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Pobieram warianty…</span>
            ) : si.error ? (
              <span style={{ fontSize: 12, color: '#b91c1c' }} title={si.error}>{'✖ ' + String(si.error).slice(0, 60)}</span>
            ) : (
              <span style={{ fontSize: 12, color: '#6b7280' }}>— bez wariantów —</span>
            )}
          </td>
          <td style={{ ...s.td, whiteSpace: 'nowrap', opacity: dim }}>
            {v ? fmtPrice(v.price, v.currency) : priceCell(o)}
          </td>
          <td data-b17="varqty" style={{ ...s.td, whiteSpace: 'nowrap' }}>
            {v ? (
              <b style={{ color: v.stock === 0 ? '#b91c1c' : '#111827', opacity: dim }}>{v.stock}</b>
            ) : stockFallback(o, si)}
          </td>
          {i === 0 && (
            <td rowSpan={N} style={{ ...s.td, fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDT(o.gmt_modified)}</td>
          )}
          {i === 0 && (
            <td rowSpan={N} style={s.td}>
              <a data-ae-link="1" href={aeUrl(o.product_id)} target="_blank" rel="noreferrer" title="Link do produktu na AliExpress"
                style={{ fontSize: 11, color: '#1d4ed8', textDecoration: 'none' }}>Link</a>{' '}
              <button onClick={() => openDetails(o.product_id)} style={{ ...s.btnGhost, fontSize: 12, padding: '5px 10px' }}>Szczegóły</button>
            </td>
          )}
        </tr>
      );
    }
    return <React.Fragment key={o.product_id}>{trs}</React.Fragment>;
  };

  // ---------------- B16F: hurtowa zmiana ilosci per wariant ----------------

  const openQtyBulk = async () => {
    const targets = selArr;
    if (!targets.length) return;
    qtyCancel.current = false;
    setQtyOpen(true); setQtyPhase('load'); setQtyItems([]); setQtyAll({});
    setQtyRun({ done: 0, total: targets.length, current: '' });
    const items: QtyItem[] = [];
    for (let i = 0; i < targets.length; i++) {
      if (qtyCancel.current) break;
      const o = targets[i];
      setQtyRun(r => ({ ...r, done: i, current: o.subject || String(o.product_id) }));
      try {
        const resp = await axios.get(API + '/aliexpress-offers/' + o.product_id, auth());
        const d: AeProduct = resp.data;
        const e: { [k: string]: string } = {};
        d.skus.forEach(k => { e[k.sku_id] = String(k.stock ?? ''); });
        items.push({ pid: o.product_id, subject: d.subject || o.subject, image: d.image || o.image, skus: d.skus, edit: e });
        setStocks(prev => {
          const kk = String(o.product_id);
          if (prev[kk] && !prev[kk].error) return prev;
          return { ...prev, [kk]: {
            total: d.skus.reduce((a, k) => a + (k.stock || 0), 0),
            n: d.skus.length,
            variants: d.skus.map(k => ({ sku_id: k.sku_id, sku_code: k.sku_code, ean: k.ean,
              price: k.price, currency: k.currency, label: k.label, stock: k.stock, active: k.active, image: k.image })),
          } };
        });
      } catch (e: any) {
        items.push({ pid: o.product_id, subject: o.subject, image: o.image, skus: [], edit: {}, error: e?.response?.data?.detail || 'błąd pobierania szczegółów' });
      }
      setQtyItems([...items]);
      await new Promise<void>(res => setTimeout(res, 250));
    }
    setQtyPhase('form');
  };

  const qtyChangedCount = (it: QtyItem): number => {
    let n = 0;
    it.skus.forEach(k => {
      const v = it.edit[k.sku_id];
      if (v == null || String(v).trim() === '') return;
      const st = parseInt(String(v), 10);
      if (!isNaN(st) && st !== (k.stock ?? 0)) n++;
    });
    return n;
  };

  const qtyTotalChanged = (): number => qtyItems.reduce((a, it) => a + qtyChangedCount(it), 0);

  const qtyApplyAll = (pid: number) => {
    const v = (qtyAll[String(pid)] || '').trim();
    if (v === '') return;
    const st = parseInt(v, 10);
    if (isNaN(st) || st < 0) return;
    setQtyItems(prev => prev.map(it => {
      if (it.pid !== pid) return it;
      const e: { [k: string]: string } = {};
      it.skus.forEach(k => { e[k.sku_id] = String(st); });
      return { ...it, edit: e };
    }));
  };

  const saveQtyBulk = async () => {
    for (const it of qtyItems) {
      for (const k of it.skus) {
        const v = it.edit[k.sku_id];
        if (v == null || String(v).trim() === '') continue;
        const st = parseInt(String(v), 10);
        if (isNaN(st) || st < 0) { setErr('Niepoprawna ilość ("' + v + '") w: ' + (it.subject || it.pid)); return; }
      }
    }
    const targets = qtyItems.filter(it => qtyChangedCount(it) > 0);
    if (!targets.length) { setBulkLog(['Brak zmian do zapisania (puste pole = bez zmian).']); return; }
    qtyCancel.current = false;
    setQtyPhase('run');
    setQtyRun({ done: 0, total: targets.length, current: '' });
    const log: string[] = [];
    let ok = 0, fail = 0, warn = 0;
    for (let i = 0; i < targets.length; i++) {
      if (qtyCancel.current) { log.push('⏹ Przerwano po ' + i + ' z ' + targets.length); break; }
      const it = targets[i];
      setQtyRun(r => ({ ...r, done: i, current: it.subject || String(it.pid) }));
      try {
        const skus: any[] = [];
        it.skus.forEach(k => {
          const v = it.edit[k.sku_id];
          if (v == null || String(v).trim() === '') return;
          const st = parseInt(String(v), 10);
          if (!isNaN(st) && st !== (k.stock ?? 0)) skus.push({ sku_id: k.sku_id, sku_code: k.sku_code || undefined, inventory: st });
        });
        if (!skus.length) { ok++; log.push('⏭ ' + (it.subject || it.pid) + ' (bez zmian)'); continue; }
        const r = await axios.patch(API + '/aliexpress-offers/' + it.pid, { skus }, auth());
        const w = (r.data?.errors) || [];
        if (w.length) { warn++; log.push('⚠ ' + (it.subject || it.pid) + ': ' + w.join(' · ')); }
        else { ok++; log.push('✔ ' + (it.subject || it.pid) + ' — ' + skus.length + ' ' + warLabel(skus.length)); }
      } catch (e: any) {
        fail++; log.push('✖ ' + (it.subject || it.pid) + ': ' + (e?.response?.data?.detail || 'błąd'));
      }
      await new Promise<void>(res => setTimeout(res, 300));
    }
    setQtyOpen(false);
    setQtyPhase('form');
    setBulkLog(['✔ Ilości zapisane: ' + ok + ' / ' + targets.length
      + (warn ? ' · ⚠ niepotwierdzone przez AE: ' + warn : '')
      + (fail ? ' · ✖ błędy: ' + fail : ''), '— szczegóły —', ...log]);
    setSel({});
    if (searchMode) { setQ(q); } else { load(page); }
    const pids = targets.map(t => String(t.pid));
    refreshStocks(pids);
    window.setTimeout(() => refreshStocks(pids), 9000);
  };

  const building = searchMode && idxInfo != null && idxInfo.state === 'building';

  return (
    <div>
      {/* naglowek + filtry */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, flex: '1 1 320px' }}>
            <input data-b17="searchbox" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Szukaj: EAN, SKU, nr produktu lub nazwa…"
              style={{ ...s.input, flex: 1 }} />
            <button onClick={doSearch} style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>Szukaj</button>
            {q && (
              <button onClick={clearSearch} title="Wyczyść wyszukiwanie" style={s.btnGhost}>✕</button>
            )}
          </div>
          <select value={statusF} onChange={e => { setStatusF(e.target.value); setSel({}); }} style={s.input}>
            <option value="onSale">Aktywne (onSale)</option>
            <option value="offline">Nieaktywne (offline)</option>
          </select>
          <button onClick={refresh} disabled={loading || searchLoading} style={s.btnGhost}>
            {loading || searchLoading ? '…' : '↻ Odśwież'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {searchMode ? (
            <>
              {'Szukanie: „' + q + '” · wyników: ' + total + (offers.length < total ? ' (pokazano ' + offers.length + ')' : '')}
              {idxInfo && idxInfo.state === 'ready' && idxInfo.built_at
                ? ' · indeks EAN/SKU z ' + fmtDT(idxInfo.built_at) + ' (' + (idxInfo.products ?? '?') + ' prod.)'
                : ''}
              {idxInfo && idxInfo.stale ? ' · odświeżam indeks w tle' : ''}
              {' · nazwy po polsku (z wyszukiwania)'}
            </>
          ) : (
            <>
              {total} produktów · strona {page} z {pages}
              {' · nazwy na liście po angielsku, w szczegółach po polsku (tak zwraca API AliExpress)'}
            </>
          )}
        </div>
        {building && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
            ⏳ Buduję indeks EAN/SKU po stronie AliExpress
            {idxInfo && (idxInfo.total ?? 0) > 0 ? ' — ' + (idxInfo.done ?? 0) + ' / ' + idxInfo.total : ''}
            {' — pierwsze wyszukiwanie może potrwać ~1 min; odświeżam automatycznie…'}
          </div>
        )}
        {err && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>{err}</div>}
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, padding: '6px 10px' }}>{msg}</div>}
      </div>

      {/* pasek edycji masowej — pojawia się po zaznaczeniu */}
      {selArr.length > 0 && !bulkRun.running && (
        <div data-b16d="bulkbar" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          <b style={{ fontSize: 13 }}>☑ Zaznaczono produktów: {selArr.length}</b>
          <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}
            onClick={() => { setBulkForm({ price: '', stock: '' }); setBulkLog([]); setBulkOpen(true); }}>✏️ Edytuj masowo</button>
          <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} title="Zobacz i ustaw ilość każdego wariantu osobno"
            onClick={openQtyBulk}>🔢 Ilości wariantów</button>
          <button style={{ ...s.btn, background: '#b45309', color: '#fff' }}
            onClick={() => { if (window.confirm('Ustawić stan 0 na wszystkich wariantach ' + selArr.length + ' zaznaczonych produktów? Oferty staną się niedostępne (wyprzedane).')) runBulk(true); }}>⏸ Dezaktywuj (stan 0)</button>
          <button style={s.btnGhost} onClick={() => setSel({})}>✕ Wyczyść zaznaczenie</button>
        </div>
      )}

      {/* wynik edycji masowej */}
      {bulkLog.length > 0 && !bulkRun.running && (
        <div data-b16d="bulklog" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <b>Wynik edycji masowej</b>
            <button style={{ ...s.btnGhost, padding: '4px 10px' }} onClick={() => setBulkLog([])}>✕ Zamknij</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0 24px' }}>
            {bulkLog.map((l, i) => (
              <div key={i} style={{ padding: '2px 0', color: l.indexOf('✖') === 0 ? '#b91c1c' : l.indexOf('✔') === 0 ? '#166534' : l.indexOf('⚠') === 0 ? '#92400e' : l.indexOf('⏭') === 0 ? '#92400e' : '#374151' }}>{l}</div>
            ))}
          </div>
        </div>
      )}

      {/* tabela */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 34 }}>
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} title="Zaznacz / odznacz wszystkie na stronie" />
              </th>
              <th style={s.th}>Nazwa · nr produktu</th>
              <th data-b17="varcol" style={s.th}>Wariant — SKU / EAN</th>
              <th style={{ ...s.th, width: 110 }}>Cena</th>
              <th data-b17="qtycol" style={{ ...s.th, width: 70 }}>Ilość</th>
              <th style={s.th}>Zmodyfikowano</th>
              <th style={{ ...s.th, width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {offers.map(renderOffer)}
            {building && offers.length === 0 && (
              <tr><td style={{ ...s.td, color: '#92400e', textAlign: 'center', padding: 24 }} colSpan={7}>
                ⏳ Buduję indeks EAN/SKU (pierwsze wyszukiwanie){idxInfo && (idxInfo.total ?? 0) > 0 ? ': ' + (idxInfo.done ?? 0) + ' / ' + idxInfo.total : ''}…
              </td></tr>
            )}
            {!building && (loading || searchLoading) && offers.length === 0 && (
              <tr><td style={{ ...s.td, color: '#6b7280', textAlign: 'center', padding: 24 }} colSpan={7}>Pobieram z AliExpress…</td></tr>
            )}
            {!loading && !searchLoading && !building && offers.length === 0 && (
              <tr><td style={{ ...s.td, color: '#6b7280', textAlign: 'center', padding: 24 }} colSpan={7}>Brak produktów{q ? ' dla zapytania „' + q + '”' : ''}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* paginacja (tylko tryb listy) */}
      {!searchMode && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', margin: '12px 0' }}>
          <button onClick={() => load(page - 1)} disabled={page <= 1 || loading} style={s.btnGhost}>‹ Poprzednia</button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Strona {page} / {pages}</span>
          <button onClick={() => load(page + 1)} disabled={page >= pages || loading} style={s.btnGhost}>Następna ›</button>
        </div>
      )}

      {/* modal: szczegoly + edycja */}
      {(det || detLoading) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
          onClick={() => { if (!saving) { setDet(null); setDetLoading(false); } }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 940, width: '100%', padding: 18 }}
            onClick={e => e.stopPropagation()}>
            {detLoading || !det ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Pobieram szczegóły z AliExpress…</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{det.subject || '(bez nazwy)'}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      ID {det.product_id} · status: {det.status || '—'} · kategoria: {det.category_id || '—'} ·
                      {' '}zmodyfikowano: {fmtDT(det.gmt_modified)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={det.url} target="_blank" rel="noreferrer" style={{ ...s.btnGhost, textDecoration: 'none', display: 'inline-block' }}>Otwórz na AliExpress ↗</a>
                    <button onClick={() => { setDet(null); setDetLoading(false); }} disabled={saving} style={s.btnGhost}>✕</button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, fontSize: 12, color: '#374151', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div><b>Cena bazowa:</b> {fmtPrice(det.price, det.currency)}</div>
                  <div><b>Waga:</b> {det.weight_kg != null ? det.weight_kg + ' kg' : '—'}</div>
                  <div><b>Opakowanie (D×S×W):</b> {det.package_lwh && det.package_lwh[0] != null ? det.package_lwh.join(' × ') + ' cm' : '—'}</div>
                  <div><b>Warianty:</b> {det.skus.length}</div>
                </div>

                {det.images.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, paddingBottom: 4 }}>
                    {det.images.map((u, i) => (
                      <img key={i} src={u} alt="" loading="lazy" style={{ width: 56, height: 56, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, flexShrink: 0, background: '#f9fafb' }} />
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 13, fontWeight: 600, margin: '6px 0' }}>Warianty (SKU) — edycja ceny i stanu:</div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={s.th}>Wariant</th>
                        <th style={s.th}>SKU / EAN</th>
                        <th style={{ ...s.th, width: 110 }}>Cena</th>
                        <th style={{ ...s.th, width: 90 }}>Stan</th>
                        <th style={{ ...s.th, width: 70 }}>Aktywny</th>
                      </tr>
                    </thead>
                    <tbody>
                      {det.skus.map(k => (
                        <tr key={k.sku_id}>
                          <td style={s.td}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              {k.image && (
                                <img src={k.image} alt="" loading="lazy" style={{ width: 34, height: 34, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb' }} />
                              )}
                              <div>
                                <div>{k.label || k.sku_id}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{k.sku_id}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>
                            {k.sku_code || '—'}{k.ean ? <div style={{ color: '#6b7280' }}>{k.ean}</div> : null}
                          </td>
                          <td style={s.td}>
                            <input value={edit[k.sku_id]?.price ?? ''} disabled={saving}
                              onChange={e => setEdit(prev => ({ ...prev, [k.sku_id]: { ...prev[k.sku_id], price: e.target.value } }))}
                              inputMode="decimal" style={{ ...s.input, width: 90, textAlign: 'right' }} />
                          </td>
                          <td style={s.td}>
                            <input value={edit[k.sku_id]?.stock ?? ''} disabled={saving}
                              onChange={e => setEdit(prev => ({ ...prev, [k.sku_id]: { ...prev[k.sku_id], stock: e.target.value } }))}
                              inputMode="numeric" style={{ ...s.input, width: 70, textAlign: 'right' }} />
                          </td>
                          <td style={{ ...s.td, color: k.active ? '#065f46' : '#b91c1c' }}>{k.active ? 'tak' : 'nie'}</td>
                        </tr>
                      ))}
                      {det.skus.length === 0 && (
                        <tr><td style={{ ...s.td, color: '#6b7280', textAlign: 'center', padding: 18 }} colSpan={5}>Produkt bez wariantów.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>zmian: {changedCount()}</span>
                  <button onClick={() => { setDet(null); setDetLoading(false); }} disabled={saving} style={s.btnGhost}>Zamknij</button>
                  <button onClick={saveDetails} disabled={saving || changedCount() === 0}
                    style={{ ...s.btn, background: '#1d4ed8', color: '#fff', opacity: (saving || changedCount() === 0) ? 0.6 : 1 }}>
                    {saving ? 'Zapisuję…' : 'Zapisz zmiany'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* modal: edycja masowa */}
      {bulkOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 560, width: '100%', padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>✏️ Edycja masowa — {selArr.length} {prodLabel(selArr.length)}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Ustawioną cenę/stan aplikuję do <b>wszystkich wariantów</b> każdego zaznaczonego produktu. Puste pole = bez zmian.
              Po zapisie sprawdzam, czy AliExpress rzeczywiście zastosował wartości.
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <label style={s.label}>Cena ({selArr[0]?.currency || 'PLN'})
                <input value={bulkForm.price} disabled={bulkRun.running} inputMode="decimal"
                  onChange={e => setBulkForm(f => ({ ...f, price: e.target.value }))} style={s.input} placeholder="np. 129.90" />
              </label>
              <label style={s.label}>Stan
                <input value={bulkForm.stock} disabled={bulkRun.running} inputMode="numeric"
                  onChange={e => setBulkForm(f => ({ ...f, stock: e.target.value }))} style={s.input} placeholder="np. 100" />
              </label>
            </div>
            {bulkRun.running && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: Math.round((bulkRun.done / Math.max(1, bulkRun.total)) * 100) + '%', height: '100%', background: '#1d4ed8', transition: 'width .2s' }} />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {bulkRun.done}/{bulkRun.total} · {bulkRun.current}
                </div>
              </div>
            )}
            {!bulkRun.running && bulkLog.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12, background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 6, padding: 8, marginBottom: 10 }}>
                {bulkLog.map((l, i) => <div key={i} style={{ whiteSpace: 'normal' }}>{l}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {bulkRun.running ? (
                <button onClick={() => { bulkCancel.current = true; }} style={{ ...s.btn, background: '#b91c1c', color: '#fff' }}>Przerwij</button>
              ) : (
                <>
                  <button onClick={() => setBulkOpen(false)} style={s.btnGhost}>Zamknij</button>
                  <button onClick={() => runBulk()} style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>Zastosuj do {selArr.length}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* modal — hurtowa zmiana ilości per wariant (zaznaczone produkty) */}
      {qtyOpen && (
        <div data-b16f="qtymodal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
          onClick={() => { if (qtyPhase === 'form') setQtyOpen(false); }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 720, width: '100%', padding: 18 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              🔢 Ilości wariantów — {selArr.length} {prodLabel(selArr.length)}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Ustaw stan każdego wariantu osobno. Puste pole = bez zmian. Zapis idzie tym samym mechanizmem co dotychczas (batch inventory AliExpress).
            </div>

            {(qtyPhase === 'load' || qtyPhase === 'run') && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: Math.round((qtyRun.done / Math.max(1, qtyRun.total)) * 100) + '%', height: '100%', background: '#1d4ed8', transition: 'width .2s' }} />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {qtyPhase === 'load' ? 'Wczytuję warianty: ' : 'Zapisuję: '}{qtyRun.done}/{qtyRun.total} · {qtyRun.current}
                </div>
              </div>
            )}

            {qtyPhase === 'form' && (
              <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }}>
                {qtyItems.map(it => (
                  <div key={it.pid} style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ width: 30, height: 30, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {it.image ? (
                          <img src={it.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ fontSize: 9, color: '#9ca3af' }}>brak</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.subject || '(bez nazwy)'}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>{it.pid}</div>
                      </div>
                      {!it.error && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          ustaw wszystkie:
                          <input value={qtyAll[String(it.pid)] || ''} inputMode="numeric" placeholder="np. 50"
                            onChange={e => setQtyAll(prev => ({ ...prev, [String(it.pid)]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') qtyApplyAll(it.pid); }}
                            style={{ ...s.input, width: 70, textAlign: 'right' }} />
                          <button onClick={() => qtyApplyAll(it.pid)} style={{ ...s.btnGhost, fontSize: 11, padding: '4px 8px' }}>Ustaw</button>
                        </div>
                      )}
                    </div>
                    {it.error ? (
                      <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>✖ {it.error}</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '6px 18px' }}>
                        {it.skus.map(k => {
                          const v = it.edit[k.sku_id];
                          const changed = v != null && String(v).trim() !== '' && !isNaN(parseInt(String(v), 10)) && parseInt(String(v), 10) !== (k.stock ?? 0);
                          return (
                            <div key={k.sku_id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                              {k.image && (
                                <img src={k.image} alt="" loading="lazy" style={{ width: 24, height: 24, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 4, background: '#f9fafb' }} />
                              )}
                              <span style={{ color: '#374151', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.label || k.sku_id}>{k.label || k.sku_id}</span>
                              <span style={{ color: '#6b7280' }}>jest: <b style={{ color: k.stock === 0 ? '#b91c1c' : '#111827' }}>{k.stock}</b></span>
                              <span style={{ color: '#9ca3af' }}>→</span>
                              <input value={v ?? ''} inputMode="numeric"
                                onChange={e => setQtyItems(prev => prev.map(x => x.pid === it.pid ? { ...x, edit: { ...x.edit, [k.sku_id]: e.target.value } } : x))}
                                style={{ ...s.input, width: 64, textAlign: 'right', ...(changed ? { borderColor: '#1d4ed8', borderWidth: 2 } : {}) }} />
                            </div>
                          );
                        })}
                        {it.skus.length === 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>Produkt bez wariantów.</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
              {qtyPhase === 'run' ? (
                <button onClick={() => { qtyCancel.current = true; }} style={{ ...s.btn, background: '#b91c1c', color: '#fff' }}>Przerwij</button>
              ) : qtyPhase === 'load' ? (
                <button onClick={() => { qtyCancel.current = true; setQtyOpen(false); }} style={s.btnGhost}>Anuluj</button>
              ) : (
                <>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>zmian: {qtyTotalChanged()}</span>
                  <button onClick={() => setQtyOpen(false)} style={s.btnGhost}>Zamknij</button>
                  <button onClick={saveQtyBulk} disabled={qtyTotalChanged() === 0}
                    style={{ ...s.btn, background: '#1d4ed8', color: '#fff', opacity: qtyTotalChanged() === 0 ? 0.6 : 1 }}>
                    💾 Zapisz ilości{qtyTotalChanged() > 0 ? ' (' + qtyTotalChanged() + ')' : ''}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
